import {
  useCameraOccupancyLatest,
  usePresenceRules,
  usePresenceZones,
} from '@hakimo-ui/hakimo/data-access';
import { CameraDetail } from '@hakimo-ui/hakimo/types';
import { CrossingsCard } from './CrossingsCard';
import { OccupancyCard } from './OccupancyCard';
import { PresenceRules } from './PresenceRules';
import { PresenceSwitch } from './PresenceSwitch';
import { PresenceZonesEditor } from './PresenceZonesEditor';

interface Props {
  camera: CameraDetail;
}

/**
 * The Presence tab: the switch, zones, the rules asked of them, and how full
 * they are.
 *
 * The switch comes first because everything below it is inert while it is
 * off. Zones come next because nothing else here is useful without one to
 * point at.
 */
export function Presence({ camera }: Props) {
  const latest = useCameraOccupancyLatest(camera.id);
  const rules = usePresenceRules(camera.id);
  const zones = usePresenceZones(camera.id);
  const hasLines = (zones.data ?? []).some((z) => z.kind === 'line');

  return (
    <div className="space-y-8">
      <PresenceSwitch camera={camera} config={rules.data} />
      <Section
        title="Zones"
        subtitle="Draw the areas to count people in, and the lines to watch crossings of."
      >
        <PresenceZonesEditor camera={camera} rules={rules.data?.rules ?? []} />
      </Section>
      <Section
        title="Rules"
        subtitle="What to alert on. Each rule is one sentence about one zone."
      >
        <PresenceRules camera={camera} liveness={latest.data?.liveness} />
      </Section>
      <Section
        title="Occupancy"
        subtitle="Last observed count per zone, and the day so far."
      >
        <OccupancyCard
          latest={latest.data}
          isLoading={latest.isLoading}
          rules={rules.data?.rules ?? []}
        />
      </Section>
      {hasLines && (
        <Section
          title="Crossings"
          subtitle="Per line, which way people went in the last day."
        >
          <CrossingsCard
            zones={zones.data ?? []}
            nowUtc={latest.data?.nowUtc}
          />
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-onlight-text-1 dark:text-ondark-text-1 text-base font-semibold">
        {title}
      </h3>
      <p className="text-onlight-text-3 dark:text-ondark-text-3 mb-3 text-sm">
        {subtitle}
      </p>
      {children}
    </section>
  );
}

export default Presence;
